import { useRef, useEffect, useCallback, type Ref } from 'react';
import { createPortal } from 'react-dom';
import { usePopoverPositioning } from '@/hooks/usePopoverPositioning';
import { OverlayScrollArea } from '@/components/ui/OverlayScrollArea/OverlayScrollArea';
import './ContextMenu.css';

/** После смены left/top панель «переезжает» под курсором — браузер шлёт mouseleave, хотя указатель ещё «логически» над подменю/строкой. */
const LEAVE_SUPPRESS_AFTER_POSITION_MS = 380;

interface ContextMenuSubmenuProps {
  /** Элемент-триггер (пункт меню), относительно которого позиционируется сабменю */
  anchorEl: HTMLElement | null;
  children: React.ReactNode;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  /** Выше родительского сабменю при вложенности (по умолчанию 1100) */
  zIndex?: number;
  /**
   * ref на корневой fixed-контейнер портала (для родительского сабменю: не закрываться,
   * когда курсор над вложенным подменю).
   */
  popoverRootRef?: Ref<HTMLDivElement | null>;
  /**
   * Портал вложенного подменю: пока курсор внутри — не считать pointer «вне» этого сабменю.
   */
  nestedPointerRootRef?: Ref<HTMLElement | null>;
}

function assignRef<T>(ref: Ref<T | null> | undefined, value: T | null) {
  if (!ref) return;
  if (typeof ref === 'function') ref(value);
  else (ref as React.MutableRefObject<T | null>).current = value;
}

function readMutableRef<T>(ref: Ref<T | null> | undefined): T | null {
  if (!ref || typeof ref === 'function') return null;
  return (ref as React.MutableRefObject<T | null>).current;
}

export function ContextMenuSubmenu({
  anchorEl,
  children,
  onMouseEnter,
  onMouseLeave,
  zIndex = 1100,
  popoverRootRef,
  nestedPointerRootRef,
}: ContextMenuSubmenuProps) {
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const pointerRef = useRef({ x: 0, y: 0 });
  const suppressLeaveUntilRef = useRef(0);

  const bumpSuppressLeave = useCallback(() => {
    suppressLeaveUntilRef.current = Date.now() + LEAVE_SUPPRESS_AFTER_POSITION_MS;
  }, []);

  useEffect(() => {
    if (!anchorEl) return;
    const onMove = (e: PointerEvent) => {
      pointerRef.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => window.removeEventListener('pointermove', onMove);
  }, [anchorEl]);

  usePopoverPositioning({
    anchorEl,
    popoverRef,
    isOpen: !!anchorEl,
    priorityAxis: 'x',
    preferredPositionX: 'right',
    verticalAlign: 'start',
    offset: 4,
    verticalOffset: -8,
    margin: { top: 20, bottom: 20, left: 20, right: 20 },
    zIndex,
    onPositionChange: bumpSuppressLeave,
    observePopoverResize: false,
    observeTriggerResize: false,
    repositionOnScroll: false,
  });

  const handleWrapperMouseLeave = useCallback(() => {
    if (Date.now() < suppressLeaveUntilRef.current) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (Date.now() < suppressLeaveUntilRef.current) return;
        const { x, y } = pointerRef.current;
        const el = document.elementFromPoint(x, y);
        const wrap = popoverRef.current;
        const anc = anchorEl;
        const nestedEl = readMutableRef(nestedPointerRootRef);
        if (el) {
          if (wrap?.contains(el)) return;
          if (anc?.contains(el)) return;
          if (nestedEl?.contains(el)) return;
        }
        onMouseLeave();
      });
    });
  }, [anchorEl, onMouseLeave, nestedPointerRootRef]);

  const setPopoverNode = useCallback(
    (node: HTMLDivElement | null) => {
      popoverRef.current = node;
      assignRef(popoverRootRef, node);
    },
    [popoverRootRef]
  );

  const content = (
    <div
      ref={setPopoverNode}
      style={{
        position: 'fixed',
        left: -9999,
        top: -9999,
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={handleWrapperMouseLeave}
    >
      <OverlayScrollArea defer={false} className="context-menu context-menu_submenu" role="menu">
        {children}
      </OverlayScrollArea>
    </div>
  );

  return createPortal(content, document.body);
}
